import { useEffect, useState } from 'react'

import {
  Box,
  Icon,
  Text,
  Title,
  WizardStep,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import { type SelectOption } from '@factorialco/deprecated-design-system'
import { OfflineAccessTokenFfDocument } from 'generated/resources/graphql'

import SelectField from 'modules/core/components/SelectField'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { DatevWizardData } from 'modules/payroll_integrations_datev_lauds/lib/types'

import { LegalEntityCard } from './LegalEntityCard'
import { LegalEntityFiller } from './legalEntityFiller'

const CompanyStep = ({ data, onDataChanged }: WizardStepRenderParams<DatevWizardData>) => {
  const [errorStates, setErrorStates] = useState<Set<string>>(new Set())

  const {
    access: { id: accessId },
    company: { id: companyId, legalEntities },
  } = useCurrentSession()

  useEffect(() => {
    setErrorStates(new Set())
  }, [data.answers.consultant_id, data.answers.payroll_software])

  const { data: offlineAccessTokenFeature } = useQuery(OfflineAccessTokenFfDocument, {
    variables: {
      companyId: companyId,
    },
  })

  useEffect(() => {
    // Try to retrieve stored form data
    const storedData = localStorage.getItem('datev_lauds_form_data')
    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData)
        if (parsedData.answers) {
          onDataChanged({
            ...data,
            answers: {
              ...data.answers,
              ...parsedData.answers,
            },
          })
        }
      } catch (e) {
        localStorage.removeItem('datev_lauds_form_data')
      } finally {
        localStorage.removeItem('datev_lauds_form_data')
      }
    }
  }, [])

  useEffect(() => {
    if (offlineAccessTokenEnabled) {
      onDataChanged({
        ...data,
        answers: {
          ...data.answers,
          hasErrors: (errorStates.size > 0).toString(),
        },
      })
    }
  }, [errorStates])

  useOnNext(() => {
    const legalEntitiesKeys = Object.keys(data.answers).filter((key) =>
      key.startsWith('legal_entity_')
    )

    const mappedLegalEntityCount = legalEntitiesKeys.map((key) => !!data.answers[key]).length
    if (data.answers.consultant_id && mappedLegalEntityCount > 0) {
      track(trackingEvents.payrollIntegrationsDatevLauds.datevLaudsSettingsConsultantIDSelected),
        {
          company_id: companyId,
          access_id: accessId,
        }

      track(trackingEvents.payrollIntegrationsDatevLauds.datevLaudsSettingsClientIdMapped),
        {
          company_id: companyId,
          access_id: accessId,
          legal_entities_mapped: mappedLegalEntityCount,
        }
    }
  })

  const handleError = (legalEntityId: string, hasError: boolean) => {
    setErrorStates((prev) => {
      const newStates = new Set(prev)
      if (hasError) {
        newStates.add(legalEntityId)
      } else {
        newStates.delete(legalEntityId)
      }
      return newStates
    })
  }

  const softwareOptions: SelectOption[] = [
    {
      label: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.payroll_software.options.datev_lodas'
      ),
      value: 'datev_lodas',
    },
    {
      label: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.payroll_software.options.datev_lug'
      ),
      value: 'datev_lug',
    },
  ]

  const offlineAccessTokenEnabled =
    offlineAccessTokenFeature?.features?.featuresConnection?.nodes[0]?.enabled || false

  return (
    <>
      <Box flexDirection="row" gap="s96">
        <Box gap="s24">
          <Title
            title={i18n.t(
              'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.steps.company.title'
            )}
            description={i18n.t(
              'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.steps.company.description'
            )}
          />
          {errorStates.size > 0 && (
            <Box
              width="s600"
              background={'radicalRed200'}
              paddingX="s24"
              paddingY="s16"
              borderRadius={{
                all: 'abs008',
              }}
              gap="s4"
            >
              <Box gap="s4" flexDirection="row">
                <Icon.Medium icon={'alert-circle-line'} color={'radicalRed1000'} />
                <Text.Header3>
                  {i18n.t(
                    'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.steps.business_unit.error_banner.title'
                  )}
                </Text.Header3>
              </Box>
              <Text.Normal size="200">
                {i18n.t(
                  'payroll_integrations.incidences.datev_lauds.hiring.error.generic_error_message'
                )}
              </Text.Normal>
            </Box>
          )}
          <Box justifyContent="flexStart" flexDirection="column" gap="s32">
            <Box justifyContent="flexStart" flexDirection="column" gap="s32">
              <TextField
                label={i18n.t(
                  'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.steps.company.consultant_text_label'
                )}
                value={data.answers.consultant_id}
                onChange={(value) => {
                  onDataChanged({
                    ...data,
                    answers: {
                      ...data.answers,
                      consultant_id: value.toString(),
                    },
                  })
                }}
                placeholder="1234567"
                maxLength={7}
              />
              <SelectField
                label={i18n.t(
                  'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.steps.company.payroll_software_label'
                )}
                options={softwareOptions}
                none={i18n.t(
                  'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.payroll_software.options.none'
                )}
                value={data.answers.payroll_software}
                onChange={(value) => {
                  onDataChanged({
                    ...data,
                    answers: {
                      ...data.answers,
                      payroll_software: value,
                    },
                  })
                }}
              />
              {legalEntities.map((legalEntity) => (
                <>
                  {!offlineAccessTokenEnabled && (
                    <LegalEntityFiller
                      key={legalEntity.id}
                      data={data}
                      onDataChanged={onDataChanged}
                      legalEntity={legalEntity}
                      completed
                      loading={false}
                      onError={(hasError) => handleError(legalEntity.id.toString(), hasError)}
                    />
                  )}
                  {offlineAccessTokenEnabled && (
                    <LegalEntityCard
                      key={legalEntity.id}
                      data={data}
                      legalEntity={legalEntity}
                      completed
                      loading={false}
                      onDataChanged={onDataChanged}
                    />
                  )}
                </>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  )
}

export const companyStep = (): WizardStep<DatevWizardData> => ({
  title: i18n.t(
    'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.steps.company.wizard_title'
  ),
  nextText: i18n.t('common.next'),
  render: (props) => <CompanyStep {...props} />,
  isCompleted: (data: DatevWizardData) => {
    const consultantIdFilled = !!data.answers.consultant_id

    const legalEntityKeys = Object.keys(data.answers).filter((key) =>
      /^legal_entity_\d+$/.test(key)
    )

    const allLegalEntitiesFilled =
      legalEntityKeys.length > 0 && legalEntityKeys.some((key) => !!data.answers[key])

    const payrollSoftwareFilled = !!data.answers.payroll_software

    const hasErrors = data.answers.hasErrors === 'true'

    return allLegalEntitiesFilled && consultantIdFilled && payrollSoftwareFilled && !hasErrors
  },
})
